import React, { useState, useEffect } from 'react';
import '../Css/AdminReview.css';
import { API_URL } from '../utils/Function';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faTrash, faSave, faTimes, faUser, faMapMarkerAlt,
  faQuoteLeft, faSortNumericDown, faExclamationTriangle, faImage
} from '@fortawesome/free-solid-svg-icons';

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    location: '',
    text: '',
    display_order: 0,
    image: null
  });

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/review.php/get`);
      const data = await response.json();
      if (data.success) {
        setReviews(data.reviews);
      } else {
        toast.error(data.error || 'Failed to fetch reviews');
      }
    } catch (error) {
      toast.error('Network error fetching reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'image') {
      const file = files[0];
      if (file) {
        setFormData(prev => ({ ...prev, image: file }));
        setImagePreview(URL.createObjectURL(file));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleOpenModal = (review = null) => {
    if (review) {
      setEditMode(true);
      setSelectedReview(review);
      setFormData({
        name: review.name,
        location: review.location,
        text: review.text,
        display_order: review.display_order,
        image: null
      });
      if (review.image) {
        setImagePreview(`${API_URL}/uploads/reviews/${review.image}`);
      } else {
        setImagePreview(null);
      }
    } else {
      setEditMode(false);
      setFormData({
        name: '',
        location: '',
        text: '',
        display_order: reviews.length,
        image: null
      });
      setImagePreview(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedReview(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData();
    form.append('name', formData.name);
    form.append('location', formData.location);
    form.append('text', formData.text);
    form.append('display_order', formData.display_order);
    if (formData.image) {
      form.append('image', formData.image);
    }

    const url = editMode
      ? `${API_URL}/review.php/put/${selectedReview.id}`
      : `${API_URL}/review.php/post`;

    try {
      const response = await fetch(url, {
        method: 'POST', // Backend handles PUT as POST if id is present in URI
        body: form
      });
      const data = await response.json();
      if (data.success) {
        toast.success(editMode ? 'Review updated' : 'Review added');
        handleCloseModal();
        fetchReviews();
      } else {
        toast.error(data.error || 'Operation failed');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/review.php/delete/${deleteId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Review deleted');
        fetchReviews();
      } else {
        toast.error(data.error || 'Delete failed');
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="admin-reviews-container">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="admin-reviews-header">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h1 className="admin-reviews-heading">
                <FontAwesomeIcon icon={faQuoteLeft} className="me-3" />
                Manage Reviews
              </h1>
            </div>
            <div className="col-md-6 text-end">
              <button className="add-review-btn" onClick={() => handleOpenModal()}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add New Review
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-5">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-5">
            <h3>No reviews found. Add your first review!</h3>
          </div>
        ) : (
          <div className="row g-4">
            {reviews.map(review => (
              <div key={review.id} className="col-lg-4 col-md-6">
                <div className="admin-review-card">
                  <div className="review-order-badge">
                    <FontAwesomeIcon icon={faSortNumericDown} className="me-1" />
                    Order: {review.display_order}
                  </div>
                  {review.image ? (
                    <img
                      src={`${API_URL}/uploads/reviews/${review.image}`}
                      alt={review.name}
                      className="admin-review-photo"
                    />
                  ) : (
                    <div className="admin-review-fallback">
                      <FontAwesomeIcon icon={faUser} />
                    </div>
                  )}
                  <h3 className="admin-review-name">{review.name}</h3>
                  <div className="admin-review-location">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-primary" />
                    {review.location}
                  </div>
                  <p className="admin-review-text">"{review.text}"</p>

                  <div className="admin-review-footer">
                    <button className="btn btn-outline-primary" onClick={() => handleOpenModal(review)}>
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button className="btn btn-outline-danger" onClick={() => handleDeleteClick(review.id)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="admin-modal-backdrop" onClick={handleCloseModal}>
          <div className="admin-modal-dialog" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FontAwesomeIcon icon={editMode ? faEdit : faPlus} className="me-2" />
                  {editMode ? 'Edit Review' : 'Add New Review'}
                </h5>
                <button type="button" className="btn-close text-white border-0 bg-transparent" onClick={handleCloseModal}>×</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <label className="form-label">Client Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Location</label>
                      <input
                        type="text"
                        className="form-control"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-12">
                      <label className="form-label">Review Text</label>
                      <textarea
                        className="form-control"
                        name="text"
                        rows="4"
                        value={formData.text}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Display Order</label>
                      <input
                        type="number"
                        className="form-control"
                        name="display_order"
                        value={formData.display_order}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Client Photo</label>
                      <input
                        type="file"
                        className="form-control"
                        name="image"
                        accept="image/*"
                        onChange={handleInputChange}
                      />
                      {imagePreview && (
                        <div className="mt-2">
                          <img src={imagePreview} alt="Preview" style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover' }} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <FontAwesomeIcon icon={faSave} className="me-1" />
                    {editMode ? 'Update' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="admin-modal-backdrop">
          <div className="admin-modal-dialog" style={{ maxWidth: '400px' }}>
            <div className="modal-content">
              <div className="modal-header bg-danger">
                <h5 className="modal-title">Confirm Delete</h5>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this review? This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={handleConfirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
